import * as ng from 'angular';
import { CellComponent } from 'tabulator-tables';
import { ActionRegistry, GridConfigDto } from '../../../../../libs/massia-tabulator/adapter/grid-config-adapter.types';
import { MassiaFilter, MassiaPagination, MassiaSort } from '../../../../../libs/massia-tabulator/helpers/mapping.tabulator.params.helpers';
import { MassiaAfterLoadCallback, MassiaFetchCallback } from '../../../../../libs/massia-tabulator/massia.tabulator.type';
import {
	IDataShareItem,
	IDataShareService,
	IFacturesService,
	IFacturesXService,
	IFactureX,
	IFactureXResult,
	IGlobalizationManagementService,
	ILodash,
	IMassiaGridApi,
	IModalService,
	IMoment,
	IMOPService,
	INotificationService,
	ISearchingFormFacturesX,
	ISiteCom,
	ISocieteCom
} from './factures-x.types';

export default class FacturesXController {
	public gridApi: Partial<IMassiaGridApi> = {};
	public serverConfig: GridConfigDto | null = null;
	public itemsByPageOptions = [20, 100, 200, 1000];
	public itemsByPage = this.itemsByPageOptions[0];
	public loading = false;
	public factures: IFactureX[] = [];
	public totalFacturesCount = 0;
	public isCheckAllFactures = false;
	public isSearchOngletOpen = true;
	public searchingFormFacturesX: ISearchingFormFacturesX = {} as ISearchingFormFacturesX;
	private readonly generatingFactureXIds = new Set<number>();
	public societesComLoaded: ISocieteCom[] = [];
	public sitesComLoaded: ISiteCom[] = [];
	public dateFormat: string;
	public slideMargin = {
		'margin-right': '0px',
		'transition-duration': '0.5s',
		'transition-animation': 'margin-right'
	};

	/* @ngInject */
	constructor(
		private readonly $window: ng.IWindowService,
		private readonly $scope: ng.IScope,
		private readonly $timeout: ng.ITimeoutService,
		private readonly $translate: ng.translate.ITranslateService,
		private readonly FacturesService: IFacturesService,
		private readonly FacturesXService: IFacturesXService,
		private readonly notification: INotificationService,
		private readonly _: ILodash,
		private readonly DataShareService: IDataShareService,
		private readonly moment: IMoment,
		private readonly globalizationManagementService: IGlobalizationManagementService,
		private readonly MOPService: IMOPService,
		private readonly $uibModal: ng.ui.bootstrap.IModalService,
		private readonly ModalService: IModalService
	) {
		this.dateFormat = this.globalizationManagementService.getCurrentLanguage().dateFormat;
		this.initSearchingForm();
		this.initUI();
	}

	private initSearchingForm(): void {
		this.searchingFormFacturesX.entity = 'factureX';
		this.searchingFormFacturesX.societeComIdSelected = null;
		this.searchingFormFacturesX.siteComIdSelected = null;
		this.searchingFormFacturesX.showErrors = false;

		const maDate = new Date();
		const y = maDate.getFullYear();
		const m = maDate.getMonth();
		const firstDay = new Date(y, m, 1);
		const lastDay = new Date(y, m + 1, 0);
		this.searchingFormFacturesX.dateDebut = this.moment(firstDay).format(this.dateFormat);
		this.searchingFormFacturesX.dateFin = this.moment(lastDay).format(this.dateFormat);
	}

	private initUI(): void {
		this.slideMargin = {
			'margin-right': '0px',
			'transition-duration': '0.5s',
			'transition-animation': 'margin-right'
		};
	}

	public async $onInit(): Promise<void> {
		if (this._.isEmpty(this.DataShareService.savedData) === false) {
			const item = this._.find(this.DataShareService.savedData as IDataShareItem[], { entity: 'factureX' });
			if (item != null) {
				await this.updateSearchForm();
			}
		}

		await this.getSocietesCom();
		await this.getSitesCom();
		await this.initServerGridConfig();
	}

	public $onDestroy(): void {
		this.MOPService.resetMop();
	}

	private async getSocietesCom(): Promise<void> {
		this.societesComLoaded = await this.FacturesService.getSocietesCom();
		if (this.societesComLoaded.length > 0 && !this.searchingFormFacturesX.societeComIdSelected) {
			this.searchingFormFacturesX.societeComIdSelected = this.societesComLoaded[0].id;
		}
	}

	public async getSitesCom(): Promise<void> {
		this.searchingFormFacturesX.siteComIdSelected = null;
		this.sitesComLoaded = await this.FacturesService.getSitesCom(this.searchingFormFacturesX.societeComIdSelected);
		if (this.gridApi && this.gridApi.setData) {
			this.gridApi.setData();
		}
	}

	private async updateSearchForm(): Promise<void> {
		this.searchingFormFacturesX = this.DataShareService.getDataShare('factureX') as ISearchingFormFacturesX;
	}

	public loadFactures(): void {
		if (this.gridApi && this.gridApi.setData) {
			this.gridApi.setData();
		}
	}

	public async initServerGridConfig(): Promise<void> {
		this.serverConfig = await this.FacturesXService.getGridConfig('FACTUREX');

		if (!this.serverConfig) return;

		if (this.gridApi.initServerGridConfig) {
			await this.gridApi.initServerGridConfig({
				configFn: () => Promise.resolve(this.serverConfig as GridConfigDto),
				ajaxURL: `${__configuration.apiUrl}/massia/factures`,
				dataShareKey: 'factureX',
				getCurrentContext: () => this.searchingFormFacturesX,
				onAfterHttpAction: () => this.loadFactures(),
				fetchFn: ((filters: MassiaFilter[], sorts: MassiaSort[], pagination: MassiaPagination) => {
					const mandatoryFilters: MassiaFilter[] = [
						{ criterion: 'idEtatFacture', type: 'In', value: [1, 3] }
						//{ criterion: 'prixHT', type: 'Ge', value: 0 }
					];

					return this.FacturesService.getFactures(
						[...mandatoryFilters, ...(filters || [])],
						sorts,
						pagination,
						this.searchingFormFacturesX.societeComIdSelected || 0,
						this.searchingFormFacturesX.siteComIdSelected || 0,
						this.searchingFormFacturesX.dateDebut,
						this.searchingFormFacturesX.dateFin
					);
				}) as MassiaFetchCallback,
				onAfterLoad: ((response: unknown) => {
					const typedResponse = response as { items?: IFactureX[]; total?: number } | IFactureX[];
					this.$scope.$applyAsync(() => {
						this.factures = Array.isArray(typedResponse) ? typedResponse : typedResponse.items ?? [];
						this.totalFacturesCount =
							!Array.isArray(typedResponse) && typedResponse.total !== undefined ? typedResponse.total : this.factures.length;
					});
				}) as MassiaAfterLoadCallback,
				customActions: {
					onCheckChange: (row?: unknown, checked?: unknown) => {
						const rowData = row as IFactureX | undefined;
						if (rowData && rowData._selectAll) {
							this.checkAllFactures(checked as boolean);
						}
					},
					generate: (row?: unknown) => {
						const rowData = row as IFactureX | undefined;
						if (rowData) this.generateFactureXUnitaire(rowData);
					},
					download: (row?: unknown) => {
						const rowData = row as IFactureX | undefined;
						if (rowData) this.FacturesXService.downloadFactureX(rowData.id, rowData.code);
					},
					formatter_status: (cell?: unknown) => this.getFactureXFormatters().formatStatus(cell as CellComponent)
				} as unknown as ActionRegistry
			});
		}
	}

	public getFactureXFormatters(): { formatStatus: (cell: CellComponent) => string } {
		return {
			formatStatus: (cell: CellComponent) => {
				const data = cell.getData() as IFactureX;
				if (data.idEtatFactureX === 1) {
					// Generee
					return `<span class="label label-success">${this.$translate.instant('FACTURES.FACTURE_X.GENERATED')}</span>`;
				} else if (data.idEtatFactureX === 0) {
					// Erreur
					return `<span class="label label-danger">${this.$translate.instant('FACTURES.FACTURE_X.ERROR')}</span>`;
				} else {
					// Non generee = 2
					return `<span class="label label-default">${this.$translate.instant('FACTURES.FACTURE_X.PENDING')}</span>`;
				}
			}
		};
	}

	public checkAllFactures(value: boolean): void {
		this.factures.forEach((facture) => {
			facture.isChecked = value;
		});
		this.isCheckAllFactures = value;
	}

	public async generateFacturesX(): Promise<void> {
		const selectedFactures = this.factures.filter((f) => f.isChecked);
		if (selectedFactures.length === 0) {
			this.notification.error(this.$translate.instant('FACTURES.FACTURE_X.NOCHECKED_GENERATE'));
			return;
		}

		const factureIds = selectedFactures.filter((f) => f.idEtatFactureX !== 1).map((f) => f.id);
		if (factureIds.length === 0) {
			this.notification.error(this.$translate.instant('FACTURES.FACTURE_X.NOCHECKED_GENERATE'));
			return;
		}

		this.loading = true;
		const generatePromise = this.FacturesXService.generateFactureX(factureIds);
		const progressModal = this.$uibModal.open({
			component: 'facturesXProgressModal',
			resolve: {
				factureIds: () => factureIds
			},
			size: 'md',
			backdrop: 'static',
			keyboard: false
		});

		let results: IFactureXResult[] = [];
		let hasError = false;

		try {
			results = await generatePromise;
		} catch {
			hasError = true;
		}

		// Laisser la barre de progression visible un court instant avant de fermer la modale et de traiter le résultat
		await this.$timeout(1500);

		this.loading = false;
		progressModal.close();

		if (hasError) {
			this.notification.error(this.$translate.instant('FACTURES.FACTURE_X.GENERATE_BATCH_ERROR'));
			this.loadFactures();
			return;
		}

		const errors = results.filter((r) => !r.success);
		if (errors.length > 0) {
			this.openErrorReportModal(results);
		} else {
			this.notification.success(this.$translate.instant('FACTURES.FACTURE_X.GENERATE_BATCH_SUCCESS'));
			this.loadFactures();
		}
	}

	public async generateFactureXUnitaire(facture: IFactureX): Promise<void> {
		if (this.generatingFactureXIds.has(facture.id)) return;

		this.generatingFactureXIds.add(facture.id);
		try {
			const results = await this.FacturesXService.generateFactureXUnitaire(facture.id);
			const errors = (results || []).filter((r) => !r.success);
			if (errors.length > 0) {
				this.openErrorReportModal(results);
			} else {
				this.notification.success(this.$translate.instant('FACTURES.FACTURE_X.GENERATE_SUCCESS'));
				this.loadFactures();
			}
		} catch {
			this.notification.error(this.$translate.instant('FACTURES.FACTURE_X.GENERATE_ERROR'));
		} finally {
			this.generatingFactureXIds.delete(facture.id);
		}
	}

	public canDownloadOrDelete(): boolean {
		return this.factures.some((f) => f.isChecked && f.idEtatFactureX === 1);
	}

	public canGenerate(): boolean {
		return this.factures.some((f) => f.isChecked && f.idEtatFactureX !== 1);
	}

	public async downloadFacturesX(): Promise<void> {
		// Traiter uniquement les factures dont le Factur-X est déjà généré
		const selectedFactures = this.factures.filter((f) => f.isChecked && f.idEtatFactureX === 1);
		if (selectedFactures.length === 0) {
			this.notification.error(this.$translate.instant('FACTURES.FACTURE_X.NOCHECKED_GENERATE'));
			return;
		}

		this.loading = true;
		if (selectedFactures.length === 1) {
			await this.FacturesXService.downloadFactureX(selectedFactures[0].id, selectedFactures[0].code);
		} else {
			await this.FacturesXService.downloadListFactureX(selectedFactures.map((f) => f.id));
		}

		this.loading = false;
	}

	public async deleteFacturesX(): Promise<void> {
		// Traiter uniquement les factures dont le Factur-X est déjà généré
		const selectedFactures = this.factures.filter((f) => f.isChecked && f.idEtatFactureX === 1);
		if (selectedFactures.length === 0) {
			this.notification.error(this.$translate.instant('FACTURES.FACTURE_X.NOCHECKED_GENERATE'));
			return;
		}

		const modalInstance = this.ModalService.confirm({
			modalTitle: this.$translate.instant('FACTURES.FACTURE_X.DELETE_CONFIRM_TITLE'),
			modalMsg: this.$translate.instant('FACTURES.FACTURE_X.DELETE_CONFIRM'),
			headerClass: 'modal-danger'
		});

		try {
			await modalInstance.result;
		} catch {
			return;
		}

		this.loading = true;
		try {
			await this.FacturesXService.deleteListFactureX(selectedFactures.map((f) => f.id));
			this.notification.success(this.$translate.instant('FACTURES.FACTURE_X.DELETE_SUCCESS'));
			this.loadFactures();
		} catch {
			this.notification.error(this.$translate.instant('FACTURES.FACTURE_X.DELETE_ERROR'));
		} finally {
			this.loading = false;
		}
	}

	private openErrorReportModal(results: IFactureXResult[]): void {
		const modalInstance = this.$uibModal.open({
			component: 'facturesXErrorReportModal',
			resolve: {
				results: () => results
			},
			size: 'lg'
		});

		modalInstance.result.then(() => {
			this.loadFactures();
		});
	}
}
